import { stripPlanProteinBlock } from "@/lib/mealPlanProteinGrams";

/** Hidden tags emitted by kin-chat for save-to-MyKin detection. */
export const MEAL_PLAN_TAG = "[MEAL_PLAN]";
export const RECIPE_TAG = "[RECIPE]";

export type MealPlanType = "meal_plan" | "recipe" | null;

/** Detect which save action (if any) applies to a Kin response. */
export function detectMealPlanType(content: string): MealPlanType {
  if (content.includes(MEAL_PLAN_TAG)) return "meal_plan";
  if (content.includes(RECIPE_TAG)) return "recipe";
  return null;
}

/** Strip the hidden tags so they never render in chat. */
export function stripMealPlanTags(content: string): string {
  // #1789 — the hidden protein block comes off FIRST, ahead of the whitespace
  // collapse below, so removing a multi-line block cannot leave a run of blank
  // lines in the bubble.
  //
  // Stripped UNCONDITIONALLY and deliberately not behind the display switch:
  // that switch decides whether a NUMBER appears on a meal plan card, never
  // whether raw JSON is allowed to show up in a chat bubble. The save path
  // reads the grams out of the block before this runs.
  return stripPlanProteinBlock(content)
    .split(MEAL_PLAN_TAG).join("")
    .split(RECIPE_TAG).join("")
    // Remove the structured [GROCERY_LIST]...[/GROCERY_LIST] machine-readable
    // block — the AI also emits a visible grouped grocery section above it,
    // so dropping the marker block prevents duplicate items in the bubble.
    .replace(/\[\/?GROCERY_LIST\]\n?/gi, "")
    // Remove the [RECIPE_TITLE]...[/RECIPE_TITLE] machine-readable marker —
    // the visible recipe body already contains a human-readable title.
    .replace(/\[RECIPE_TITLE\][\s\S]*?\[\/RECIPE_TITLE\]/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trimEnd();
}

const DAY_NAMES = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
];

export interface ParsedMealDay {
  day: string;
  meals: {
    name: string;
    description?: string;
    /**
     * Kin's protein estimate for this meal, in grams. #1789.
     *
     * Attached by `attachPlanProteinGrams` from the hidden [PROTEIN_JSON]
     * block, NOT parsed out of the prose — the prose deliberately never
     * contains a number. Absent on every plan saved before #1789 and on any
     * meal whose name the block did not match, and that absence is a real,
     * visible state: #1781's EntryRow renders null as an empty field and a
     * genuine 0 as "0".
     */
    protein_g?: number | null;
  }[];
}

/** Strip emoji + leading/trailing markdown from a meal label. */
function cleanMealLine(raw: string): string {
  return raw
    .replace(/\*\*/g, "")
    .replace(/^[\s\-\*•\d.]+/, "")
    .trim();
}

/**
 * Parse a Kin meal-plan response into structured day → meals.
 * Looks for lines starting with a day name (Monday, Tuesday, …) and
 * captures everything after the first colon as the meal entry.
 * Stops at the grocery list section.
 */
export function parseMealPlanDays(content: string): ParsedMealDay[] {
  // Cut off everything from the grocery list onward
  const groceryIdx = content.search(/🛒\s*GROCERY LIST:/i);
  const body = groceryIdx >= 0 ? content.slice(0, groceryIdx) : content;

  const lines = body.split("\n");
  const days: ParsedMealDay[] = [];
  let current: ParsedMealDay | null = null;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Match e.g. "**Monday**: Chicken stir fry" or "Monday — Tacos" or "Monday: ..."
    const dayMatch = line.match(/^[*_#>\s\-]*\**\s*([A-Za-z]+)\**\s*[:\-—–]\s*(.+)$/);
    if (dayMatch) {
      const day = dayMatch[1];
      if (DAY_NAMES.includes(day.toLowerCase())) {
        current = {
          day: day.charAt(0).toUpperCase() + day.slice(1).toLowerCase(),
          meals: [],
        };
        days.push(current);
        const rest = cleanMealLine(dayMatch[2]);
        if (rest) current.meals.push({ name: rest });
        continue;
      }
    }

    // Continuation lines: bullet underneath an active day
    if (current && /^[\-\*•]/.test(line)) {
      const cleaned = cleanMealLine(line);
      if (cleaned) current.meals.push({ name: cleaned });
    }
  }

  return days;
}

export interface ParsedRecipe {
  title: string;
  description: string;
}

/** Title-case "MAKE-YOUR-OWN TACO BAR" → "Make-Your-Own Taco Bar". */
function toTitleCase(s: string): string {
  return s
    .toLowerCase()
    .split(/(\s+|-)/)
    .map((part) => (/^\s+$/.test(part) || part === "-" ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join("");
}

const RECIPE_NAME_BLOCKLIST = [
  /^(here'?s|here is|girl|mama|okay|ok|alright|listen|sis|babe|honey|love)\b/i,
  /^(i feel|i got|i've got|let'?s|let me|first|so,|so |you got|you'?re)/i,
  /^(your|the|this|today)'?s?\s+(sanity|dinner|lunch|breakfast|recipe|meal)/i,
];

function looksConversational(line: string): boolean {
  const stripped = line.replace(/\*\*/g, "").trim();
  if (!stripped) return true;
  if (stripped.length > 70) return true;
  if (/[!?]\s*$/.test(stripped)) return true;
  if (/\bsoul\b|\bvibes?\b|\bobsessed\b|\bsanity\b/i.test(stripped)) return true;
  return RECIPE_NAME_BLOCKLIST.some((re) => re.test(stripped));
}

/** Build a fallback "April 18" date label. */
function dateLabel(date = new Date()): string {
  const month = date.toLocaleString("en-US", { month: "long" });
  return `${month} ${date.getDate()}`;
}

/**
 * Section headers Kin commonly emits inside a recipe body. We must NEVER
 * accept these as the recipe title even if they match a heading / bold /
 * all-caps pattern. Compare case-insensitively against the line stripped of
 * whitespace, colons, and trailing punctuation.
 */
const SECTION_HEADER_DENYLIST = new Set<string>([
  "ingredients",
  "instructions",
  "directions",
  "steps",
  "method",
  "notes",
  "tips",
  "serving",
  "for serving",
  "nutrition",
  "sauce",
  "marinade",
  "dressing",
  "toppings",
  "garnish",
  "for the sauce",
  "for the cake",
  "for the filling",
  "for the topping",
  "for the dough",
  "for the crust",
  "for the glaze",
  "for the marinade",
]);

function isSectionHeader(candidate: string): boolean {
  const cleaned = candidate
    .replace(/\*\*/g, "")
    .replace(/[:\.\!\?,;]+$/g, "")
    .trim()
    .toLowerCase();
  if (!cleaned) return true;
  if (SECTION_HEADER_DENYLIST.has(cleaned)) return true;
  // Catch "For the lemon glaze", "For the chicken marinade", "For sauce", etc.
  if (/^for(\s+the)?\s+\S+/.test(cleaned)) return true;
  return false;
}

/** Extract a recipe title + short description from a Kin response. */
export function parseRecipe(content: string): ParsedRecipe {
  const groceryIdx = content.search(/🛒\s*GROCERY LIST:/i);
  let body = (groceryIdx >= 0 ? content.slice(0, groceryIdx) : content).trim();
  // Strip hidden tags so they cannot be mistaken for a title in the fallback scan.
  body = stripPlanProteinBlock(body)
    .replace(/\[RECIPE\]/gi, "")
    .replace(/\[MEAL_PLAN\]/gi, "")
    .replace(/\[\/?GROCERY_LIST\]\n?/gi, "")
    .replace(/\[TODO_LIST[^\]]*\][\s\S]*?\[\/TODO_LIST\]/gi, "")
    .replace(/\[\/?TODO_LIST(?::[a-z_]+)?\]/gi, "")
    .trim();

  let title = "";
  let description = "";

  // Step 1 (dormant primary): structured [RECIPE_TITLE]...[/RECIPE_TITLE] tag.
  // The kin-chat system prompt no longer requires this tag, but keep parsing
  // it so any legacy or edge-case response that still emits it works cleanly.
  const tagMatch = body.match(/\[RECIPE_TITLE\]([\s\S]*?)\[\/RECIPE_TITLE\]/i);
  if (tagMatch) {
    const candidate = tagMatch[1]
      .replace(/\*\*/g, "")
      .replace(/[:\s]+$/, "")
      .trim();
    if (candidate) title = candidate;
  }

  // Step 2 (main path): pre-April-19 behavior. Kin now formats recipes with
  // the dish name as a bolded heading on line 1, so grab lines[0], strip
  // bold/colon noise, and accept it unless it matches the section-header
  // denylist (e.g. "Ingredients", "For the sauce").
  if (!title) {
    const allLines = body.split("\n").map((l) => l.trim()).filter(Boolean);
    if (allLines.length > 0) {
      const first = allLines[0]
        .replace(/^#+\s*/, "")
        .replace(/^\*\*/, "")
        .replace(/\*\*$/, "")
        .replace(/\*\*/g, "")
        .replace(/[:\s]+$/, "")
        .trim();
      if (first && !isSectionHeader(first) && !looksConversational(first)) {
        title = first;
      }
    }
  }

  // Step 3 (fallback): scan lines for a heading / bold-only / all-caps title,
  // skipping section headers via the denylist.
  if (!title) {
    const lines = body.split("\n").map((l) => l.trim()).filter(Boolean);
    for (const line of lines) {
      const headingMatch = line.match(/^#+\s*(.+?)\s*#*$/);
      if (headingMatch) {
        const candidate = headingMatch[1].replace(/\*\*/g, "").trim();
        if (candidate && !looksConversational(candidate) && !isSectionHeader(candidate)) {
          title = candidate;
          break;
        }
      }
      // Bold-only line: **Recipe Name**
      const boldOnlyMatch = line.match(/^\*\*(.+?)\*\*[:\s]*$/);
      if (boldOnlyMatch) {
        const candidate = boldOnlyMatch[1].trim();
        if (candidate && !looksConversational(candidate) && !isSectionHeader(candidate)) {
          title = candidate;
          break;
        }
      }
      // All-caps line (e.g. "MAKE-YOUR-OWN TACO BAR")
      const stripped = line.replace(/\*\*/g, "").replace(/[:\s]+$/, "").trim();
      if (
        stripped.length >= 4 &&
        stripped.length <= 60 &&
        stripped === stripped.toUpperCase() &&
        /[A-Z]/.test(stripped) &&
        !looksConversational(stripped) &&
        !isSectionHeader(stripped)
      ) {
        title = toTitleCase(stripped);
        break;
      }
    }
  }

  // Fallback
  if (!title) title = `Recipe - ${dateLabel()}`;


  // Description: prefer a recipe-type tag (e.g., "Gluten & Dairy Free") or a meal category.
  const typeTags: string[] = [];
  if (/\bgluten[-\s]?free\b/i.test(body)) typeTags.push("Gluten Free");
  if (/\bdairy[-\s]?free\b/i.test(body)) typeTags.push("Dairy Free");
  if (/\bnut[-\s]?free\b/i.test(body)) typeTags.push("Nut Free");
  if (/\begg[-\s]?free\b/i.test(body)) typeTags.push("Egg Free");
  if (/\bvegan\b/i.test(body)) typeTags.push("Vegan");
  if (/\bvegetarian\b/i.test(body)) typeTags.push("Vegetarian");
  if (typeTags.length > 0) {
    description = typeTags.slice(0, 3).join(" & ");
  } else {
    const categoryMatch = body.toLowerCase().match(/\b(breakfast|brunch|lunch|dinner|dessert|snack|appetizer|side dish|sauce)\b/);
    description = categoryMatch
      ? categoryMatch[1].charAt(0).toUpperCase() + categoryMatch[1].slice(1)
      : "Recipe";
  }

  return { title, description };
}

/** Build a "Meal Plan - April 18" style title for new meal plans. */
export function buildWeekOfTitle(date = new Date()): string {
  return `Meal Plan - ${dateLabel(date)}`;
}

/* ------------------------------------------------------------------ */
/* Conversational-text stripping for saved content                     */
/* ------------------------------------------------------------------ */

const DAY_START_RE = /^[*_#>\s\-]*\**\s*(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i;

/**
 * Clean a recipe response down to just structured content:
 * recipe title, ingredients, and instructions. Strips Kin's
 * conversational opener and closing remarks.
 */
export function cleanRecipeContent(content: string): string {
  const groceryIdx = content.search(/🛒\s*GROCERY LIST:/i);
  const body = (groceryIdx >= 0 ? content.slice(0, groceryIdx) : content).trim();
  const lines = body.split("\n");

  let startIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i].trim();
    if (!l) continue;
    if (/^#+\s+/.test(l)) { startIdx = i; break; }
    const boldOnly = l.match(/^\*\*(.+?)\*\*[:\s]*$/);
    if (boldOnly && !looksConversational(boldOnly[1])) { startIdx = i; break; }
    const stripped = l.replace(/\*\*/g, "").replace(/[:\s]+$/, "").trim();
    if (
      stripped.length >= 4 && stripped.length <= 60 &&
      stripped === stripped.toUpperCase() && /[A-Z]/.test(stripped) &&
      !looksConversational(stripped)
    ) { startIdx = i; break; }
    if (/^(ingredients?|instructions?|steps?|directions?|method)\s*:?\s*$/i.test(l.replace(/\*\*/g, ""))) {
      startIdx = i; break;
    }
  }

  if (startIdx === -1) return body;

  // Trim trailing conversational lines.
  let endIdx = lines.length - 1;
  while (endIdx > startIdx) {
    const l = lines[endIdx].trim();
    if (!l) { endIdx--; continue; }
    if (/^[\-\*•\d]/.test(l)) break;
    if (/^#+\s+/.test(l)) break;
    if (/^\*\*.+\*\*[:\s]*$/.test(l)) break;
    if (looksConversational(l)) { endIdx--; continue; }
    break;
  }

  return lines.slice(startIdx, endIdx + 1).join("\n").trim();
}

/**
 * Clean a meal-plan response down to its day-by-day structured content,
 * preserving the 🛒 GROCERY LIST section if present. Strips Kin's
 * opening commentary and trailing chat.
 */
export function cleanMealPlanContent(content: string): string {
  const lines = content.split("\n");

  let startIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (DAY_START_RE.test(lines[i].trim())) { startIdx = i; break; }
  }
  if (startIdx === -1) return content.trim();

  const fullText = lines.slice(startIdx).join("\n");

  // Boundary priority: new [GROCERY_LIST] tag wins over the legacy
  // 🛒 GROCERY LIST: marker. This must run BEFORE stripMealPlanTags so the
  // tag is still present in `content`.
  const groceryNewIdx = fullText.search(/\[GROCERY_LIST\]/i);
  const groceryLegacyIdx = fullText.search(/🛒\s*GROCERY LIST:/i);

  let groceryIdx = -1;
  if (groceryNewIdx >= 0) {
    groceryIdx = groceryNewIdx;
  } else if (groceryLegacyIdx >= 0) {
    groceryIdx = groceryLegacyIdx;
  }

  const planBody = groceryIdx >= 0 ? fullText.slice(0, groceryIdx) : fullText;
  const grocerySection = groceryIdx >= 0 ? fullText.slice(groceryIdx) : "";

  const planLines = planBody.split("\n");
  let endIdx = planLines.length - 1;
  while (endIdx > 0) {
    const l = planLines[endIdx].trim();
    if (!l) { endIdx--; continue; }
    if (DAY_START_RE.test(l)) break;
    if (/^[\-\*•\d]/.test(l)) break;
    if (/^\*\*.+\*\*[:\s]*$/.test(l)) break;
    if (looksConversational(l)) { endIdx--; continue; }
    break;
  }

  const cleanedPlan = planLines.slice(0, endIdx + 1).join("\n").trim();
  const combined = grocerySection ? `${cleanedPlan}\n\n${grocerySection.trim()}` : cleanedPlan;

  // Final cleanup: remove any structural tags (now that we've used
  // [GROCERY_LIST] as a boundary, the marker itself shouldn't persist).
  //
  // ⚠️ #1789 — the protein block is stripped HERE too, and that matters
  // separately from the display strip. This function's output is stored as
  // `meal_plans.raw_markdown`, and `raw_markdown` is what feeds grocery list
  // generation (SavedMealPlans → onMakeRecipeGroceryList). A JSON array left in
  // there would be handed to the grocery generator as if it were plan text.
  //
  // The SAVE path reads the grams off the RAW response before calling this, so
  // stripping them here loses nothing — see ChatMessages' save handler.
  return stripPlanProteinBlock(combined)
    .split(MEAL_PLAN_TAG).join("")
    .split(RECIPE_TAG).join("")
    .replace(/\[\/?GROCERY_LIST\]\n?/gi, "")
    .replace(/\[RECIPE_TITLE\][\s\S]*?\[\/RECIPE_TITLE\]/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

